/**
 * Interrupteur maître « PAUSE PUBLICATIONS » (global + par réseau).
 * À placer en haut des pages de publication réseaux sociaux.
 * PAR DÉFAUT tout est en PAUSE (sécurité). Seul l'admin peut réactiver.
 */
import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Pause, Play, ShieldWarning } from "@phosphor-icons/react";

const NETS = [
  { key: "facebook", label: "Facebook" },
  { key: "instagram", label: "Instagram" },
  { key: "tiktok", label: "TikTok" },
];

export default function PublishingPauseBanner() {
  const [ctrl, setCtrl] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => api.get("/publishing/control").then((r) => setCtrl(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const save = async (patch) => {
    setSaving(true);
    try {
      const r = await api.put("/publishing/control", patch);
      setCtrl(r.data);
      toast.success(r.data.paused ? "Publications EN PAUSE ⏸️" : "Publications réactivées ▶️");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action réservée à l'administrateur");
    } finally { setSaving(false); }
  };

  if (!ctrl) return null;
  const paused = !!ctrl.paused;
  const nets = ctrl.networks || {};

  return (
    <div data-testid="publishing-pause-banner"
      className={`rounded-xl border p-4 mb-5 ${paused ? "border-amber-500/50 bg-amber-500/10" : "border-emerald-500/40 bg-emerald-500/[0.06]"}`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <ShieldWarning size={22} className={paused ? "text-amber-400" : "text-emerald-400"} weight="fill" />
          <div>
            <div className="font-bold text-white" data-testid="pause-status-label">
              {paused ? "⏸️ Publications EN PAUSE" : "▶️ Publications ACTIVES"}
            </div>
            <div className="text-xs text-zinc-400 mt-0.5 max-w-xl">
              {paused
                ? "Aucune publication automatique ou manuelle ne part (Facebook · Instagram · TikTok). Réactive quand tes corrections sont faites."
                : "Les publications peuvent partir. Mets en pause le temps d'exécuter des corrections."}
            </div>
          </div>
        </div>
        <button onClick={() => save({ paused: !paused })} disabled={saving} data-testid="pause-global-toggle"
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold ${paused ? "bg-emerald-600 hover:bg-emerald-500 text-white" : "bg-amber-500 hover:bg-amber-400 text-black"}`}>
          {paused ? <><Play size={16} weight="fill" /> Réactiver tout</> : <><Pause size={16} weight="fill" /> Mettre en pause</>}
        </button>
      </div>
      <div className="flex flex-wrap gap-2 mt-3 pl-1">
        {NETS.map((n) => {
          const netPaused = nets[n.key] !== false; // true = en pause
          const blocked = paused || netPaused;
          return (
            <button key={n.key} onClick={() => save({ networks: { [n.key]: !netPaused } })} disabled={saving || paused}
              data-testid={`pause-net-${n.key}`} title={paused ? "Global en pause" : ""}
              className={`text-xs px-3 py-1.5 rounded-full border ${blocked ? "border-amber-500/40 bg-amber-500/10 text-amber-300" : "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"} ${paused ? "opacity-50 cursor-not-allowed" : ""}`}>
              {n.label} · {blocked ? "en pause" : "actif"}
            </button>
          );
        })}
      </div>
    </div>
  );
}
